import { fn } from '@ember/helper';
/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import type dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import GeneralFormFieldsLocationInput from 'lumen/components/general/form-fields/location-input';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import type { SearchResults } from 'lumen/controllers/hotel/events';
import type { Category } from 'lumen/controllers/hotel/events/edit';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import type { Event as EventModel } from 'lumen/models';
import { EVENTS_CATEGORIES_WITH_LABEL_AND_ICON } from 'lumen/utils/constants/events-constants';
import { isGoogleMapsAvailable } from 'lumen/utils/google-maps';

type PlacesResponse = google.maps.places.PlaceResult;

export type ParametersForNewEvent = {
  categoryId: string;
  description: string;
  endDate: Dayjs;
  location: string;
  name: string;
  placesResponse: PlacesResponse | null;
  startDate: Dayjs;
};

interface HotelEventsFormsFormEditEventSignature {
  Args: {
    addExistingEvent: (event: EventModel) => void;
    closeModal: (refresh?: boolean) => void;
    fetchSearchResult: (name: string) => void;
    initialDate: string;
    saveNewEvent: (parameters: ParametersForNewEvent) => void;
    searchResults: SearchResults | undefined;
  };
}

export default class HotelEventsFormsFormEditEvent extends Component<HotelEventsFormsFormEditEventSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  @tracked availableCategories: Category[] = [];
  @tracked category: Category | null | undefined = null;
  @tracked description = '';
  @tracked googleIsAvailable = false;
  @tracked location = '';
  @tracked name = '';
  @tracked placesResponse: PlacesResponse | null = null;
  @tracked submitActive = true;
  @tracked tempEndDate: string | null = null;
  @tracked tempStartDate: string | null = null;

  // Should return true to add disabled=true to the submit button
  get areDateInputsInValid(): boolean {
    if (this.isDateEmpty(this.startDate) || this.isDateEmpty(this.endDate)) {
      return true;
    }

    // Start date should be before end date
    if (this.startDate.isAfter(this.endDate)) {
      return true;
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areInputsInvalid() {
    return (
      this.areDateInputsInValid ||
      !this.submitActive ||
      !this.name ||
      !this.category ||
      this.locationError?.hasError
    );
  }

  get areSuggestedEventsAvailable(): boolean {
    return (this.args.searchResults?.events?.length ?? 0) > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endDate() {
    if (this.tempEndDate) {
      return this.hotelAndMarketService.toLocalTime(this.tempEndDate);
    }

    if (this.args.initialDate) {
      return this.hotelAndMarketService.toLocalTime(this.args.initialDate);
    }

    return null;
  }

  get isEndDateAfterStartDate(): boolean {
    if (this.isDateEmpty(this.startDate) || this.isDateEmpty(this.endDate)) {
      return false;
    }

    return this.startDate.isAfter(this.endDate);
  }

  get locationError(): { hasError: boolean; message: string } | null {
    if (!this.location) {
      return {
        hasError: true,
        message: this.intl.t('general.required'),
      };
    }

    if (this.googleIsAvailable && !this.placesResponse) {
      return {
        hasError: true,
        message: this.intl.t('validation.select_valid_location'),
      };
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get startDate() {
    if (this.tempStartDate) {
      return this.hotelAndMarketService.toLocalTime(this.tempStartDate);
    }

    if (this.args.initialDate) {
      return this.hotelAndMarketService.toLocalTime(this.args.initialDate);
    }

    return null;
  }

  constructor(
    owner: Owner,
    args: HotelEventsFormsFormEditEventSignature['Args'],
  ) {
    super(owner, args);

    this.availableCategories = [
      ...EVENTS_CATEGORIES_WITH_LABEL_AND_ICON.map((event) => ({
        label: this.intl.t(event.translationKey),
        id: event.id,
      })).sort((a, b) => a.label.localeCompare(b.label)),
      {
        id: 'other',
        label: this.intl.t('events.category.other'),
      },
    ];

    this.category = this.availableCategories[0];

    this.googleIsAvailable = isGoogleMapsAvailable();
  }

  debouncedSend(): void {
    // Note: added extra check here...
    if (!this.isDestroying) {
      this.args.fetchSearchResult(this.name);
    }
  }

  debouncedSubmit(): void {
    const {
      category,
      description,
      endDate,
      location,
      name,
      placesResponse,
      startDate,
    } = this;

    if (!category || !endDate || !location || !startDate) {
      return;
    }

    this.args.saveNewEvent({
      categoryId: category.id,
      description,
      endDate,
      location,
      name,
      placesResponse,
      startDate,
    });
  }

  isDateEmpty(date: dayjs.Dayjs | null): date is null {
    return !date;
  }

  @action
  addEvent(event: EventModel): void {
    this.args.addExistingEvent(event);
  }

  @action
  endSelection(value: Dayjs): void {
    this.tempEndDate = value ? value.format('YYYY-MM-DD') : value;
  }

  @action
  fetchSearchFromController(event: Event): void {
    this.name = (event.target as HTMLInputElement).value;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    debounce(this, this.debouncedSend, 300);
  }

  @action
  newEvent(): void {
    this.submitActive = false;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    debounce(this, this.debouncedSubmit, 300);
  }

  @action
  setLocation(place: string, placesResponse?: PlacesResponse): void {
    this.location = place;
    if (placesResponse) {
      this.placesResponse = placesResponse;
    } else {
      this.placesResponse = null;
    }
  }

  @action
  startSelection(value: Dayjs): void {
    this.tempStartDate = value ? value.format('YYYY-MM-DD') : value;
  }

  @action updateDescription(event: Event): void {
    this.description = (event.target as HTMLInputElement).value;
  }

  @action updateLocation(event: Event): void {
    this.location = (event.target as HTMLInputElement).value;
  }

  <template>
    <div class="p-500 pb-0">
      <div class="flex">
        <PrismInput
          @autofocus={{true}}
          @label={{t "general.name"}}
          @onInput={{this.fetchSearchFromController}}
          @required={{true}}
          @requiredErrorMessage={{t "general.required"}}
          @value={{this.name}}
        />
      </div>
      {{#if this.areSuggestedEventsAvailable}}
        <div class="event-suggestions mb-400 p-300">
          <div class="text-300--bold mb-300 flex">
            <div>
              {{t "events.suggested_events"}}
            </div>
          </div>
          <div class="grid gap-200">
            {{#each @searchResults.events as |result|}}
              <div class="event-suggestions-item items-center gap-300">
                <div class="text-200--regular">
                  {{#if (eq result.numberOfDays 1)}}
                    {{dayjsFormat result.startDate format="L"}}
                  {{else}}
                    {{dayjsFormat result.startDate format="L"}}
                    &nbsp;-&nbsp;
                    {{dayjsFormat result.endDate format="L"}}
                  {{/if}}
                </div>
                <div class="text-200--regular">
                  {{result.name}}
                </div>
                <div class="flex justify-end">
                  <PrismButton
                    @iconBefore="plus"
                    @isSmall={{true}}
                    @label={{t "events.add_event"}}
                    @onClick={{fn this.addEvent result}}
                  />
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      {{/if}}
      <div class="flex flex-row">
        <div class="mr-400 w-1/2">
          <GeneralFormsFormFieldsDatepickerFormField
            {{! @glint-expect-error: Incorrect type }}
            @datePickerMaxDate={{this.endDate}}
            @label={{t "events.start_date"}}
            @onSelectDate={{this.startSelection}}
            @selectedDate={{this.startDate}}
          />
        </div>
        <div class="w-1/2">
          <GeneralFormsFormFieldsDatepickerFormField
            {{! @glint-expect-error: Incorrect type }}
            @datePickerMinDate={{this.startDate}}
            @errorMessage={{t "error.date_outside_range"}}
            @isInErrorState={{this.isEndDateAfterStartDate}}
            @label={{t "events.end_date"}}
            @onSelectDate={{this.endSelection}}
            @selectedDate={{this.endDate}}
          />
        </div>
      </div>
      <div class="flex flex-row">
        <div class="mr-200 flex w-1/3 flex-col">
          <label class="input-label required-indicator">
            {{t "events.category"}}
          </label>
          <PrismSelectInput
            @isSecondary={{true}}
            @onChange={{fn (mut this.category)}}
            @options={{this.availableCategories}}
            @value={{this.category}}
          />
        </div>
        <div class="ml-200 flex w-2/3 flex-col">
          {{#if this.googleIsAvailable}}
            <GeneralFormFieldsLocationInput
              @locationError={{this.locationError}}
              @setLocation={{this.setLocation}}
            />
          {{else}}
            <PrismInput
              @label={{t "events.location"}}
              @onInput={{this.updateLocation}}
              @required={{true}}
              @requiredErrorMessage={{t "general.required"}}
              @value={{this.location}}
              id="location-input"
            />
          {{/if}}
        </div>
      </div>
      <div class="flex flex-col">
        <PrismInput
          @label={{t "events.description"}}
          @onInput={{this.updateDescription}}
          @textarea={{true}}
          @value={{this.description}}
        />
      </div>
    </div>
    <div class="settings-modal__footer sticky-footer">
      <GeneralFormFieldsCancelAndConfirm
        @cancel={{@closeModal}}
        @confirm={{this.newEvent}}
        @confirmIcon="plus"
        @confirmText={{t "events.add_event"}}
        @isConfirmDisabled={{this.areInputsInvalid}}
      />
    </div>
  </template>
}
